import React, { useState, useContext, useRef, useEffect } from 'react';
import { SunIcon, MoonIcon, CheckIcon } from '@heroicons/react/24/outline';
import { UserContext } from '../context/UserContext';
import { useColorTheme, ColorTheme } from '../context/ColorThemeContext';
import { useTranslation } from 'react-i18next';

const colorOptions: { value: ColorTheme; hex: string }[] = [
  { value: 'blue',    hex: '#2563EB' },
  { value: 'violet',  hex: '#4F46E5' },
  { value: 'emerald', hex: '#047857' },
  { value: 'teal',    hex: '#0F766E' },
  { value: 'amber',   hex: '#B45309' },
  { value: 'zinc',    hex: '#18181B' },
];

const ThemeSelector: React.FC = () => {
  const { theme, updateUserTheme } = useContext(UserContext)!;
  const { colorTheme, setColorTheme } = useColorTheme();
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = colorOptions.find(o => o.value === colorTheme)!;
  const currentLabel = t(`common.theme.colors.${colorTheme}`);

  return (
    <div className="space-y-1.5">
      {/* 日 / 月 Tab */}
      <div className="flex bg-slate-100 dark:bg-slate-700/60 rounded-lg p-0.5">
        <button
          onClick={() => updateUserTheme('light')}
          title={t('common.theme.light')}
          className={`flex-1 flex items-center justify-center h-7 rounded-md transition-colors ${
            theme === 'light'
              ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          <SunIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => updateUserTheme('dark')}
          title={t('common.theme.dark')}
          className={`flex-1 flex items-center justify-center h-7 rounded-md transition-colors ${
            theme === 'dark'
              ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          <MoonIcon className="h-4 w-4" />
        </button>
      </div>

      {/* 主題色下拉 */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(v => !v)}
          className="w-full flex items-center gap-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 rounded-md px-3 h-9 text-sm text-slate-700 dark:text-gray-300 transition-colors shadow-sm"
        >
          <span className="flex items-center gap-1.5 flex-1">
            <span>{currentLabel}</span>
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: current.hex }}
            />
          </span>
          <svg
            className={`ml-1 h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {dropdownOpen && (
          <div className="absolute bottom-full left-0 mb-1.5 w-full bg-white dark:bg-slate-700 rounded-md shadow-xl border-2 border-slate-300 dark:border-slate-600 z-[70] overflow-hidden py-1">
            {colorOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setColorTheme(opt.value); setDropdownOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors text-slate-700 dark:text-gray-300 border-b border-slate-200 dark:border-slate-600 last:border-b-0"
              >
                <span className="flex items-center gap-1.5 flex-1">
                  <span>{t(`common.theme.colors.${opt.value}`)}</span>
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: opt.hex }}
                  />
                </span>
                {opt.value === colorTheme && (
                  <CheckIcon className="h-3 w-3 text-slate-400 dark:text-gray-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeSelector;
